'use client';

import { c as _c } from "react/compiler-runtime";
import type { Data } from '@repo/strapi-types';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import AppLink from '@/components/elementary/AppLink';
import { ArrowRight } from '@/components/icons/ArrowRight';
import { formatEventName, useAnalytics } from '@/lib/analytics/analytics';
import { ANALYTICS_EVENTS } from '@/lib/analytics/analytics.constants';
import { useComponentTracking } from '@/lib/analytics/component-tracking';
import { cn } from '@/lib/styles';

// Sole localStorage key: holds the dismissed announcement's `publishedAt`, so an edit re-shows it.
const DISMISS_STORAGE_KEY = 'lok:announcement-dismissed';
interface AnnouncementBannerProps {
  text: string;
  link?: Data.Component<'utilities.link'> | null;
  /** Signature of the current content; written to localStorage on dismiss. */
  signature: string;
}
export function AnnouncementBanner(t0) {
  const $ = _c(19);
  const {
    text,
    link,
    signature
  } = t0;
  const [isDismissed, setIsDismissed] = useState(false);
  const {
    track
  } = useAnalytics();
  const componentTracking = useComponentTracking();
  let t1;
  let t2;
  if ($[0] !== signature) {
    t1 = () => {
      try {
        setIsDismissed(window.localStorage.getItem(DISMISS_STORAGE_KEY) === signature);
      } catch {}
    };
    t2 = [signature];
    $[0] = signature;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== componentTracking || $[4] !== signature || $[5] !== track) {
    t3 = () => {
      track({
        eventType: formatEventName(ANALYTICS_EVENTS.ANNOUNCEMENT_DISMISSED),
        eventProperties: {
          ...(componentTracking && {
            component: componentTracking.component
          })
        }
      });
      try {
        window.localStorage.setItem(DISMISS_STORAGE_KEY, signature);
      } catch {}
      setIsDismissed(true);
    };
    $[3] = componentTracking;
    $[4] = signature;
    $[5] = track;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const dismiss = t3;
  if (isDismissed) {
    return null;
  }
  let t4;
  if ($[7] !== link) {
    t4 = link?.href ? <AppLink link={link} variant="none" iconEnd={<ArrowRight className="size-4 shrink-0" />} className="lok-text-body-bold-small inline-flex items-center gap-1 align-middle text-wolf-gray-600 underline" /> : null;
    $[7] = link;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4 || $[10] !== text) {
    t5 = <div className="lok-text-body-regular-small text-wolf-gray-900">{text}{" "}{t4}</div>;
    $[9] = t4;
    $[10] = text;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = cn("absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer", "flex size-4 items-center justify-center text-wolf-gray-900", "transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wolf-gray-600");
    t7 = <X className="size-4" />;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  let t8;
  if ($[14] !== dismiss) {
    t8 = <button type="button" onClick={dismiss} aria-label="Dismiss announcement" className={t6}>{t7}</button>;
    $[14] = dismiss;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t5 || $[17] !== t8) {
    t9 = <div data-announcement-bar={true} className="gradient-announcement-bar fixed inset-x-0 top-0 z-50 flex h-14 w-full items-center overflow-hidden lg:h-9"><div className="relative w-full px-11 text-center">{t5}{t8}</div></div>;
    $[16] = t5;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
}